import { HubConnectionState } from '@microsoft/signalr';
import { useSyncExternalStore } from 'react';

export interface SignalRHubStatus {
  name: string;
  state: HubConnectionState;
}

type SignalRStatusSnapshot = Record<string, SignalRHubStatus>;

const EMPTY_SNAPSHOT: Readonly<SignalRStatusSnapshot> = {};

let snapshot: SignalRStatusSnapshot = EMPTY_SNAPSHOT;
const listeners = new Set<() => void>();

const notify = () => {
  listeners.forEach(listener => listener());
};

/**
 * Records the connection state of the hub at the given endpoint.
 */
export const setSignalRState = (hubEndpoint: string, state: HubConnectionState) => {
  const current = snapshot[hubEndpoint];

  if (current?.name === hubEndpoint && current?.state === state) return;

  snapshot = { ...snapshot, [hubEndpoint]: { name: hubEndpoint, state } };
  notify();
};

/**
 * Clears the recorded state of every hub.
 */
export const resetSignalRState = () => {
  snapshot = EMPTY_SNAPSHOT;
  notify();
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
};

const getSnapshot = () => snapshot;

/**
 * The current connection state of every hub, keyed by hub endpoint.
 */
export const useSignalRHubs = () =>
  useSyncExternalStore(subscribe, getSnapshot, () => EMPTY_SNAPSHOT);

/**
 * The overall connection status across all hubs.
 */
export const useSignalRStatus = (): HubConnectionState | undefined => {
  const hubs = useSignalRHubs();
  const states = Object.values(hubs).map(hub => hub.state);

  if (states.length === 0) return undefined;

  const connected = states.filter(state => state === HubConnectionState.Connected).length;

  if (connected === states.length) return HubConnectionState.Connected;
  if (connected === 0) return HubConnectionState.Disconnected;

  return HubConnectionState.Connecting;
};
